/** @jsxImportSource @emotion/react */
import DateTimePicker from 'components/atoms/DateTimePicker'
import Input from 'components/atoms/Input'
import Select from 'components/atoms/Select'
import { billingClaimStatusSelectOptions } from 'components/lab/billingCharge/consts'
import { parseAdjustmentCodesInput, parseRemarkCodesInput } from 'components/lab/billingEraReports/AdjustmentCodes'
import { chargeOptionFilter, getChargeDropdownLabel } from 'components/lab/billingCharge/utils'
import { toNum } from 'components/lab/orderEdit/helpers'
import { TimeWrapper } from 'components/lab/orderEdit/PsgHstWorkflow/FollowUpForm'
import { Form, Formik } from 'formik'
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetBillingChargeQuery, useGetBillingChargesQuery } from 'store/services/billingCharges'
import { useCreateBillingPaymentMutation } from 'store/services/billingPayments'
import { useRecordManualDenialMutation } from 'store/services/billingDenials'
import { confirm } from 'components/molecules/Confirmation'
import { useGetLabPatientListQuery } from 'store/services/patient'
import { useGetLabPayersQuery } from 'store/services/payer'
import { useDebounce } from 'use-debounce'
import * as Yup from 'yup'
import {
  calculateServiceLineBalances,
  ChargePayerChainEntry,
  routePatientResponsibility,
} from 'ts-common/utils/serviceLineBalances.utils'
import {
  isWriteOffReason,
  OTHER_WRITE_OFF_REASON,
  writeOffNeedsReason,
} from 'ts-common/utils/writeOffReasons.utils'
import { BillingChargeDto, ChargeAccountRow } from 'store/types'
import {
  chargeClaimNumberFor,
  defaultChainPayer,
  PostableLine,
  prefillAmounts,
  writeOffFromAllowed,
  writeOffReasonFor,
} from './manualPostingPrefill'
import {
  emptyPaymentMethodDetails,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_METHODS,
  PaymentMethod,
  PaymentMethodDetailsFields,
  paymentMethodDetailsBody,
} from './PaymentMethodDetailsFields'
import { formatYyyyMmDd } from './ServiceLineRow'
import { todayAsServiceDate } from 'helpers/dates'
import { ChargeChainPaymentBySync, ChargePayerOption, useChargePayerChain } from './useChargePayerChain'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'
import { OrderInsuranceNotes } from 'components/lab/billingPayment/OrderInsuranceNotes'
import { WriteOffReasonField } from './WriteOffReasonField'

type Props = {
  open: boolean
  onClose: () => void
  patientId: number
  onDone?: () => void
  defaultPayerId?: number
  defaultPaymentDate?: string
  // Opened from a charge on the Payments tab: that charge is picked and its
  // lines filled in before the form shows.
  defaultChargeId?: string
  // The Payments tab's charge-account rows — the only place a line's expected
  // allowed and what was already transferred to the next payer are known.
  accountRows?: ChargeAccountRow[]
}

const validationSchema = Yup.object().shape({
  patientId: Yup.number().required('Required'),
  paymentByPayerId: Yup.number().required('Required'),
  paymentDate: Yup.string().required('Required'),
  paymentMethod: Yup.mixed<PaymentMethod>()
    .oneOf(PAYMENT_METHODS)
    .required('Required'),
  checkNumber: Yup.string().when('paymentMethod', {
    is: 'Check',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  checkDate: Yup.string().when('paymentMethod', {
    is: 'Check',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  // Same rules as the ERA posting form: an EFT is identified by its trace
  // number and settlement date; card details are typed in, so asked for but
  // not demanded.
  payerClaimControlNumber: Yup.string().optional(),
  eftTraceNumber: Yup.string().when('paymentMethod', {
    is: 'ACH',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  effectiveDate: Yup.string().when('paymentMethod', {
    is: 'ACH',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  originatingCompanyId: Yup.string().optional(),
  cardTransactionId: Yup.string().optional(),
  cardProcessedDate: Yup.string().optional(),
  cardLastFour: Yup.string()
    .matches(/^\d{4}$/, { message: 'Enter the last four digits', excludeEmptyString: true })
    .optional(),
  nextResponsiblePayerId: Yup.number().nullable().optional(),
  nextResponsiblePatientId: Yup.number().nullable().optional(),
  claimStatus: Yup.string().optional(),
  noteDraft: Yup.string().optional(),
  notes: Yup.array().of(Yup.string()).optional(),
  chargeId: Yup.string().required('Required'),
  serviceLines: Yup.array()
    .of(
      Yup.object().shape({
        cptCode: Yup.string().trim().required('CPT code is required'),
      }),
    )
    .min(1, 'At least one service line is required'),
})

const Money = ({ v }: { v?: string | number }) => (
  <span>{v ? `$${Number(v).toFixed(2)}` : '$0.00'}</span>
)

// A blank line for a procedure the charge does not list.
const emptyServiceLine = (srNo: number) => ({
  srNo,
  balance: 0,
  allowed: 0,
  paidRefund: 0,
  transfers: 0,
  copay: 0,
  writeOff: 0,
  cptCode: '',
  charged: 0,
  startDateOfService: todayAsServiceDate(),
  expectedAllowed: null as number | null,
  planBalance: 0,
  patientBalance: 0,
  writeOffReason: '',
  writeOffNote: '',
  adjustmentCodesInput: '',
  remarkCodesInput: '',
  transactions: [],
})

// The charge's own lines, filled in the way the inline Apply Payment row
// fills one: the biller only types what the EOB says.
const linesFromCharge = (
  isPrimary: boolean,
  lines: ReadonlyArray<PostableLine & { cptCode: string; startDos?: string | null }>,
) => lines.map((line, index) => {
  const amounts = prefillAmounts(isPrimary, line)

  return {
    ...emptyServiceLine(index + 1),
    cptCode: line.cptCode,
    startDateOfService: line.startDos || undefined,
    expectedAllowed: line.expectedAllowed ?? null,
    charged: amounts.baseCharged,
    balance: amounts.baseCharged,
    allowed: amounts.allowed,
    paidRefund: '',
    copay: '',
    writeOff: amounts.writeOff,
    writeOffReason: amounts.writeOffReason,
  }
})

// The charge's procedures as postable lines when the Payments tab's rows are
// not at hand (no expected allowed, nothing transferred yet).
const linesFromProcedures = (charge?: BillingChargeDto) =>
  (charge?.procedures || []).map((procedure) => ({
    cptCode: procedure.cptCode,
    startDos: procedure.startDos,
    charged: Number((toNum(procedure.planAmount) + toNum(procedure.patientAmount)).toFixed(2)),
    expectedAllowed: null,
    transactions: [],
  }))

/**
 * Fills the form in from the charge once its chain is known: the payer whose
 * turn it is, then every line of the charge with what follows from it. Runs
 * again when the charge or the paying payer changes — a downstream payer
 * starts from what was transferred, not from the charge — and never touches
 * lines the biller is already editing under the same charge and payer.
 */
const ChargeLinesAutoFill: React.FC<{
  chargeId?: string
  paymentByPayerId?: number | string
  chainLoaded: boolean
  chain: ChargePayerChainEntry[]
  charge?: BillingChargeDto
  accountRow?: ChargeAccountRow
  setFieldValue: (field: string, value: unknown) => void
}> = ({ chargeId, paymentByPayerId, chainLoaded, chain, charge, accountRow, setFieldValue }) => {
  const filledFor = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!chargeId || !chainLoaded) {
      return
    }

    const lines = accountRow?.serviceLines?.length
      ? accountRow.serviceLines
      : linesFromProcedures(charge)

    if (!paymentByPayerId) {
      const entry = defaultChainPayer(chain, lines)

      if (entry) {
        setFieldValue('paymentByPayerId', Number(entry.payerId))
      }

      return
    }

    const key = `${chargeId}|${paymentByPayerId}`

    if (filledFor.current === key) {
      return
    }

    filledFor.current = key

    const position = Number(chain.find((entry) => Number(entry.payerId) === Number(paymentByPayerId))?.position) || 1

    setFieldValue('serviceLines', linesFromCharge(position === 1, lines))
  }, [chargeId, paymentByPayerId, chainLoaded, chain, charge, accountRow, setFieldValue])

  return null
}

const ServiceLines = ({
  serviceLines,
  onChange,
  onRemove,
}: {
  serviceLines?: any[]
  onChange: (index: number, field: string, value: number | string) => void
  onRemove: (index: number) => void
}) => {
  const lines = serviceLines ?? []

  let tBalance = 0
  let tAllowed = 0
  let tPaid = 0
  let tCopay = 0
  let tWriteOff = 0

  const formatInitial = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return ''
    if (typeof value === 'number') return value.toFixed(2)
    return String(value)
  }

  const handleTextChange = (idx: number, field: string, extra?: (val: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    onChange(idx, field, raw)
    if (extra) extra(raw)
  }

  const rows = lines.map((sl, idx) => {
    tBalance += toNum(sl.balance)
    tAllowed += toNum(sl.allowed)
    tPaid += toNum(sl.paidRefund)
    tCopay += toNum(sl.copay)
    tWriteOff += toNum(sl.writeOff)

    return (
      <tr key={idx}>
        <td>{idx + 1}</td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={sl.cptCode}
            onChange={(e) => onChange(idx, 'cptCode', e.target.value)}
          />
        </td>
        <td css={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
          {formatYyyyMmDd(sl.startDateOfService)}
        </td>
        <td css={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
          {sl.expectedAllowed != null ? <Money v={sl.expectedAllowed} /> : '--'}
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.balance)}
            onChange={handleTextChange(idx, 'balance', (val) => onChange(idx, 'charged', val))}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.allowed)}
            onChange={handleTextChange(idx, 'allowed', (val) => {
              const writeOff = writeOffFromAllowed(sl.balance, val)

              onChange(idx, 'writeOff', writeOff.toFixed(2))
              onChange(idx, 'writeOffReason', writeOffReasonFor(writeOff, sl.writeOffReason))
              if (!writeOffNeedsReason(writeOff)) {
                onChange(idx, 'writeOffNote', '')
              }
            })}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.paidRefund)}
            onChange={handleTextChange(idx, 'paidRefund')}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.copay)}
            onChange={handleTextChange(idx, 'copay', (val) => onChange(idx, 'patientBalance', val))}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.writeOff)}
            onChange={handleTextChange(idx, 'writeOff', (val) => {
              // A row edited back to $0 never carries a stale "why".
              if (!writeOffNeedsReason(toNum(val))) {
                onChange(idx, 'writeOffReason', '')
                onChange(idx, 'writeOffNote', '')
              }
            })}
          />
          {/* The "why" appears only once there is an amount to explain. */}
          <WriteOffReasonField
            writeOff={sl.writeOff}
            reason={sl.writeOffReason}
            note={sl.writeOffNote}
            onReasonChange={(value) => onChange(idx, 'writeOffReason', value)}
            onNoteChange={(value) => onChange(idx, 'writeOffNote', value)}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            placeholder="CO-45"
            value={sl.adjustmentCodesInput || ''}
            onChange={(e) => onChange(idx, 'adjustmentCodesInput', e.target.value)}
          />
        </td>
        <td>
          <div className="d-flex align-items-center" css={{ gap: 8 }}>
            <input
              type="text"
              className="form-control form-control-sm rounded-lg text-md"
              placeholder="N211"
              value={sl.remarkCodesInput || ''}
              onChange={(e) => onChange(idx, 'remarkCodesInput', e.target.value)}
            />
            <Button variant="link" className="text-danger p-0" onClick={() => onRemove(idx)}>
              <i className="fa fa-trash" />
            </Button>
          </div>
        </td>
      </tr>
    )
  })

  return (
    <table className="table table-sm mb-0">
      <thead>
        <tr css={{ '& > th': { fontWeight: 500 } }}>
          <th>Sr. No</th>
          <th>CPT</th>
          <th>DOS</th>
          <th>Exp. Allowed</th>
          <th>Balance</th>
          <th>Allowed</th>
          <th>Paid/Refund</th>
          <th>Transfers</th>
          <th
            title="Payer-side write-off — reduces the plan balance, never the patient's"
            css={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
            Write-Off
          </th>
          <th>Adj. Codes</th>
          <th>Remarks</th>
        </tr>
      </thead>
      <tbody>
        {rows}
        <tr style={{ fontWeight: 600 }}>
          <td colSpan={4}>Total</td>
          <td>
            <Money v={tBalance} />
          </td>
          <td>
            <Money v={tAllowed} />
          </td>
          <td>
            <Money v={tPaid} />
          </td>
          <td>
            <Money v={tCopay} />
          </td>
          <td>
            <Money v={tWriteOff} />
          </td>
          <td colSpan={2} />
        </tr>
      </tbody>
    </table>
  )
}

export const CreatePaymentModal: React.FC<Props> = ({
  open,
  onClose,
  onDone,
  defaultPayerId,
  defaultPaymentDate,
  defaultChargeId,
  accountRows,
  patientId,
}) => {
  const [patientSearch, setPatientSearch] = React.useState<string>('')
  const [payerSearch, setPayerSearch] = React.useState<string>('')

  const [debouncedPayerSearch] = useDebounce(payerSearch, 300)

  const payerQueryParams = React.useMemo(
    () => ({ name: debouncedPayerSearch, active: true, patientId }),
    [debouncedPayerSearch, patientId],
  )

  const { data: payers } = useGetLabPayersQuery(payerQueryParams)

  const [createPayment, { isLoading }] = useCreateBillingPaymentMutation()
  const [recordManualDenial, { isLoading: isRecordingDenial }] = useRecordManualDenialMutation()

  const [chargeSearch, setChargeSearch] = React.useState<string>('')

  // The patient's charges are fetched once and filtered client-side
  // (chargeOptionFilter): the server-side orderId filter is an exact match, so
  // it could never search by order ID prefix or by charge ID at all.
  const { data: charges } = useGetBillingChargesQuery({
    page: 0,
    size: 100,
    patientId,
  })

  const payerOptions = React.useMemo(
    () =>
      (payers ?? []).map((p): ChargePayerOption => ({
        value: p.payerId,
        label: p.name,
        payerName: p.name,
        matchedName: p.matchedName,
      })),
    [payers],
  )

  const initialFormikValues = React.useMemo(
    () => ({
      patientId,
      paymentByPayerId: defaultPayerId ?? undefined,
      // Billers asked for Other as the default [TES-3208]; the details that go
      // with the method follow the same rules as the ERA posting form.
      paymentMethod: 'Other' as PaymentMethod,
      ...emptyPaymentMethodDetails(),
      paymentDate: defaultPaymentDate || new Date().toISOString(),
      claimStatus: '',
      noteDraft: '',
      notes: [] as string[],
      serviceLines: [] as ReturnType<typeof emptyServiceLine>[],
      chargeId: defaultChargeId as string | undefined,
    }),
    [patientId, defaultPayerId, defaultPaymentDate, defaultChargeId],
  )

  const [debouncedPatientSearch] = useDebounce(patientSearch, 300)
  const { data: patients } = useGetLabPatientListQuery({
    size: 100,
    page: 0,
    name: debouncedPatientSearch,
    id: patientId,
  })

  const patientOptions = React.useMemo(
    () =>
      (patients?.items ?? []).map((p) => ({
        value: String(p.id),
        label: `${p.lastName}, ${p.firstName} (${p.dob})`,
      })),
    [patients?.items],
  )

  const {
    selectedChargeId,
    setSelectedChargeId,
    isChainLoaded,
    getChargeChain,
    getChargePayerOptions,
    getDerivedNextResponsible,
    getNextResponsibleLabel,
  } = useChargePayerChain({ patientId, listedCharges: charges?.items })

  // The same request the chain hook makes (RTK Query serves both from one
  // fetch): the aggregate is what carries the charge's procedures and the
  // control numbers its claims went out under.
  const { data: selectedCharge } = useGetBillingChargeQuery(
    selectedChargeId && patientId ? { id: selectedChargeId, patientId: String(patientId) } : skipToken,
  )
  const selectedAccountRow = React.useMemo(
    () => (accountRows || []).find((row) => String(row.chargeId) === String(selectedChargeId)),
    [accountRows, selectedChargeId],
  )

  React.useEffect(() => {
    if (!open) {
      setPatientSearch('')
      setPayerSearch('')
      setSelectedChargeId(undefined)
    } else if (defaultChargeId) {
      setSelectedChargeId(defaultChargeId)
    }
  }, [open, defaultChargeId])

  const positionOf = (chargeId?: string, payerId?: number | string): number =>
    Number(getChargeChain(chargeId).find((entry) => Number(entry.payerId) === Number(payerId))?.position) || 1

  const mapCharge = (c: any) => ({
    value: String(c.id),
    label: getChargeDropdownLabel(c),
    chargeId: String(c.id),
    orderId: c.orderId,
  })

  // Charges created without an order (orderId null) are payable too.
  const chargeOptions = React.useMemo(() => (charges?.items ?? []).map(mapCharge), [charges])

  // A paper EOB or portal denial kept on the charge instead of posted: the
  // codes and amounts are recorded, no money moves.
  const saveAsDenial = async (values: typeof initialFormikValues) => {
    if (!values.chargeId || !values.paymentByPayerId || !values.serviceLines?.length) {
      toast.error('Select the charge and the denying payer, and add the denied lines')
      return
    }

    if (values.serviceLines.some((sl) => toNum(sl.paidRefund) !== 0)) {
      toast.error('A denial pays nothing. Post a payment for lines the payer paid.')
      return
    }

    try {
      await confirm('', {
        title: 'Save as denial?',
        description:
          'The codes and amounts are kept on the charge as a denial. Nothing posts: the balance stays open '
          + 'until the denial is written off, pushed to the patient or resubmitted.',
        yesAction: 'Save denial',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      await recordManualDenial({
        patientId: Number(values.patientId),
        chargeId: values.chargeId,
        paymentByPayerId: Number(values.paymentByPayerId),
        paymentMethod: values.paymentMethod,
        ...paymentMethodDetailsBody(values),
        denialDate: values.paymentDate,
        claimNumber: chargeClaimNumberFor(selectedCharge, positionOf(values.chargeId, values.paymentByPayerId)),
        notes: values.notes ?? [],
        lines: values.serviceLines.map((sl) => {
          const adjustments = parseAdjustmentCodesInput(sl.adjustmentCodesInput)
          const remarkCodes = parseRemarkCodesInput(sl.remarkCodesInput)

          return {
            cptCode: sl.cptCode,
            startDos: sl.startDateOfService || null,
            charged: toNum(sl.balance),
            allowed: toNum(sl.allowed),
            paid: 0,
            patientResponsibility: toNum(sl.copay),
            adjustments: adjustments.length ? adjustments : null,
            remarkCodes: remarkCodes.length ? remarkCodes : null,
          }
        }),
      }).unwrap()

      toast.success('Denial saved on the charge')

      if (typeof onDone === 'function') {
        onDone()
      }

      onClose()
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to save the denial')
    }
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      css={{
        '.modal-content': {
          borderRadius: 15,
        },
        '.modal-body': {
          padding: '20px 30px',
        },
      }}
      size="xl"
      centered>
      <div
        css={{
          textAlign: 'center',
          padding: '20px 0',
          fontWeight: 600,
          fontSize: 22,
        }}>
        Create Payment
      </div>
      <Formik
        key={`create-payment-${open ? 'open' : 'closed'}`}
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialFormikValues}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (!values?.serviceLines || values.serviceLines.length === 0) {
              toast.error('At least one service line is required')
              return
            }

            const payerName =
              getChargePayerOptions(values.chargeId)?.find(
                (p) => Number(p.value) === Number(values.paymentByPayerId),
              )?.payerName ||
              payerOptions.find((p) => Number(p.value) === Number(values.paymentByPayerId))?.label ||
              'Unknown'

            const derivedNextResponsible = getDerivedNextResponsible(
              values.chargeId,
              values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
            )

            // A write-off reduces what anyone can still collect, so it does
            // not go in unexplained. The server enforces the same rule.
            const unexplained = values.serviceLines.find((sl: any) =>
              writeOffNeedsReason(toNum(sl.writeOff))
              && (!isWriteOffReason(sl.writeOffReason)
                || (sl.writeOffReason === OTHER_WRITE_OFF_REASON && !String(sl.writeOffNote || '').trim())))

            if (unexplained) {
              toast.error(`A write-off reason is required for CPT ${unexplained.cptCode || 'the service line'}`)
              return
            }

            // The control number this charge was submitted under to the
            // paying payer — the claim number an ERA for it would carry, so
            // a hand posting is recorded under the same one.
            const claimNumber = chargeClaimNumberFor(
              selectedCharge,
              positionOf(values.chargeId, values.paymentByPayerId),
            )

            const serviceLinesWithTransactions = values.serviceLines.map(({
              adjustmentCodesInput,
              remarkCodesInput,
              writeOffReason,
              writeOffNote,
              expectedAllowed,
              ...sl
            }: any) => {
              const adjustments = parseAdjustmentCodesInput(adjustmentCodesInput)
              const remarkCodes = parseRemarkCodesInput(remarkCodesInput)
              // PR goes to `transfers` when a downstream payer adjudicates next
              // (COB), to `copay` when the patient is next responsible.
              const routed = routePatientResponsibility(toNum(sl.copay), derivedNextResponsible)
              const {
                charged,
                allowed,
                paidRefund,
                transfers,
                copay,
                writeOff,
                planBalance,
                patientBalance,
              } = calculateServiceLineBalances({
                charged: toNum(sl.balance),
                allowed: toNum(sl.allowed),
                paidRefund: toNum(sl.paidRefund),
                transfers: routed.transfers,
                copay: routed.copay,
                writeOff: toNum(sl.writeOff),
              })

              return {
                ...sl,
                balance: charged,
                allowed,
                paidRefund,
                transfers,
                copay,
                writeOff,
                planBalance: Number(planBalance.toFixed(2)),
                patientBalance: Number(patientBalance.toFixed(2)),
                transactions: [
                  {
                    date: values.paymentDate,
                    txId: claimNumber,
                    paymentBy: payerName,
                    allowed,
                    paidRefund,
                    transfers,
                    copay,
                    writeOff,
                    writeOffReason: writeOffReason || null,
                    writeOffNote: String(writeOffNote || '').trim() || undefined,
                    ...(adjustments.length ? { adjustments } : {}),
                    ...(remarkCodes.length ? { remarkCodes } : {}),
                  },
                ],
              }
            })

            await createPayment({
              patientId: Number(values.patientId),
              paymentByPayerId: Number(values.paymentByPayerId),
              nextResponsiblePayerId: derivedNextResponsible.type === 'PAYER'
                ? derivedNextResponsible.payerId
                : null,
              nextResponsiblePatientId: derivedNextResponsible.type === 'PATIENT'
                ? Number(values.patientId)
                : null,
              paymentMethod: values.paymentMethod,
              ...paymentMethodDetailsBody(values),
              paymentDate: values.paymentDate,
              appointmentDate: values.serviceLines?.[0]?.startDateOfService || values.paymentDate,
              notes: values.notes ?? [],
              status: 'APPLIED',
              serviceData: serviceLinesWithTransactions,
              claimNumber,
              chargeId: values.chargeId,
              claimStatus: values.claimStatus || undefined,
            }).unwrap()

            toast.success('Payment created successfully')

            if (typeof onDone === 'function') {
              onDone()
            }

            onClose()
          } catch (e: any) {
            console.log(e)
            toast.error(e?.data?.message || 'Failed to create payment')
          } finally {
            setSubmitting(false)
          }
        }}>
        {({ values, setFieldValue, isSubmitting, isValid, dirty }) => (
          <Form>
            <ChargeChainPaymentBySync
              chargeId={values.chargeId}
              paymentByPayerId={values.paymentByPayerId}
              chainLoaded={isChainLoaded(values.chargeId)}
              chain={getChargeChain(values.chargeId)}
              setFieldValue={setFieldValue}
            />
            <ChargeLinesAutoFill
              chargeId={values.chargeId}
              paymentByPayerId={values.paymentByPayerId}
              chainLoaded={isChainLoaded(values.chargeId)}
              chain={getChargeChain(values.chargeId)}
              charge={isChainLoaded(values.chargeId) ? selectedCharge : undefined}
              accountRow={selectedAccountRow}
              setFieldValue={setFieldValue}
            />
            <Modal.Body>
              <div className="row">
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Payment By</label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Payor"
                    name="paymentByPayerId"
                    options={getChargePayerOptions(values.chargeId) ?? payerOptions}
                    formatOptionLabel={formatPayerOptionLabel}
                    filterOption={filterPayerOption}
                    value={
                      (getChargePayerOptions(values.chargeId) ?? payerOptions).find(
                        (o) => Number(o.value) === Number(values.paymentByPayerId),
                      ) || null
                    }
                    onChange={(item) =>
                      setFieldValue('paymentByPayerId', item ? Number(item.value) : undefined)
                    }
                    onInputChange={(v) => setPayerSearch(v)}
                    inputValue={payerSearch}
                    onBlur={() => setPayerSearch('')}
                  />
                </div>
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Next Responsible</label>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    title="Derived from the selected charge's payer chain"
                    value={getNextResponsibleLabel(values.chargeId, values.paymentByPayerId)}
                  />
                </div>
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Claim Status</label>
                  <Select
                    value={values.claimStatus}
                    name="claimStatus"
                    handleChange={(e) => setFieldValue('claimStatus', e.target.value)}
                    options={billingClaimStatusSelectOptions}
                  />
                </div>
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Payment Date</label>
                  <TimeWrapper>
                    <DateTimePicker
                      value={values.paymentDate ? new Date(values.paymentDate) : null}
                      handleChange={(d) => {
                        setFieldValue('paymentDate', d ? d.toISOString() : '')
                      }}
                      showTimeSelect={false}
                      isClearable
                      dateFormat="MMMM d, yyyy"
                    />
                  </TimeWrapper>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6 col-md-3">
                  <label className="textGray">Charge</label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Search by Order ID or Charge ID"
                    name="chargeId"
                    options={chargeOptions}
                    filterOption={chargeOptionFilter}
                    onInputChange={(v) => setChargeSearch(v)}
                    inputValue={chargeSearch}
                    value={chargeOptions.find((o) => String(o.value) === String(values.chargeId)) || null}
                    onChange={(item) => {
                      const nextChargeId = item ? String(item.value) : undefined
                      setFieldValue('chargeId', nextChargeId)
                      setSelectedChargeId(nextChargeId)
                      // The lines belong to the charge: a new charge fills
                      // them in again, no charge leaves nothing to post.
                      setFieldValue('serviceLines', [])
                      // Payment By is reconciled by ChargeChainPaymentBySync
                      // once the charge's real chain arrives — the listing row
                      // only knows two payers and would wipe a tertiary here.
                    }}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="textGray">Patient</label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Search Patient"
                    name="patientId"
                    options={patientOptions}
                    value={
                      patientOptions.find((o) => Number(o.value) === Number(values.patientId)) ||
                      null
                    }
                    onChange={() => {}}
                    isDisabled={true}
                    onInputChange={(v) => setPatientSearch(v)}
                    inputValue={patientSearch}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="textGray">Payment Method</label>
                  <Select
                    value={values.paymentMethod}
                    name="paymentMethod"
                    noDefault
                    handleChange={(e) => setFieldValue('paymentMethod', e.target.value)}
                    options={PAYMENT_METHOD_OPTIONS}
                  />
                </div>
                <PaymentMethodDetailsFields values={values} setFieldValue={setFieldValue} />
              </div>
              <hr className="py-1" />
              <ServiceLines
                serviceLines={values?.serviceLines}
                onChange={(i, field, val) => setFieldValue(`serviceLines[${i}].${field}`, val)}
                onRemove={(idx) => {
                  const updated = (values.serviceLines || []).filter((_, i) => i !== idx)
                  const reindexed = updated.map((sl, i) => ({ ...sl, srNo: i + 1 }))
                  setFieldValue('serviceLines', reindexed)
                }}
              />
              <div className="d-flex justify-content-end mt-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const nextIndex = (values.serviceLines?.length || 0) + 1
                    setFieldValue('serviceLines', [...(values.serviceLines || []), emptyServiceLine(nextIndex)])
                  }}>
                  + Add
                </Button>
              </div>
              <div
                className="mt-3 p-3"
                css={{
                  background: '#F4F6F9',
                  borderRadius: 10,
                }}>
                <div
                  css={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}>
                  Notes
                </div>
                {Array.isArray(values.notes) && values.notes.length > 0 && (
                  <div
                    className="mt-2"
                    css={{
                      maxHeight: 300,
                      overflowY: 'scroll',
                    }}>
                    {values.notes.map((n, i) => (
                      <div key={i} className="d-flex justify-content-between align-items-center">
                        <span>- {n}</span>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => {
                            const arr = [...(values.notes ?? [])]
                            arr.splice(i, 1)
                            setFieldValue('notes', arr)
                          }}>
                          remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <OrderInsuranceNotes patientId={values.patientId} chargeId={values.chargeId} />
                <div
                  css={{
                    fontWeight: 600,
                    fontSize: 14,
                    marginTop: 10,
                  }}>
                  Add note
                </div>
                <div className="d-flex mt-1">
                  <Input
                    value={values.noteDraft}
                    name="noteDraft"
                    placeholder="Add note"
                    handleChange={(e) => setFieldValue('noteDraft', e.target.value)}
                    className="form-control mr-2"
                    parentDiv={null}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      const v = (values.noteDraft || '').trim()
                      if (v) {
                        setFieldValue('notes', [...(values.notes ?? []), v])
                        setFieldValue('noteDraft', '')
                      }
                    }}>
                    Add
                  </Button>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-end">
              <Button variant="outline-secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline-danger"
                disabled={
                  !values.chargeId || !values.paymentByPayerId || !values.serviceLines?.length
                  || isSubmitting || isLoading || isRecordingDenial
                }
                onClick={() => saveAsDenial(values)}>
                Save as Denial
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!dirty || !isValid || isSubmitting || isLoading || isRecordingDenial}>
                Create Payment
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
